import { motion } from 'framer-motion';
import { Zap, Brain, ArrowRight, Clock, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ServiceModelsSection = () => {
  return (
    <section id="leistungen" className="py-20 lg:py-32 bg-gradient-steel">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Wählen Sie Ihren Fertigungsweg
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Zwei optimierte Wege zu Ihrem fertigen Bauteil – je nach Priorität
          </p>
        </motion.div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
          {/* FastLane Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative group h-full"
          >
            <div className="absolute inset-0 bg-gradient-fastlane rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity" />
            <div className="relative bg-card rounded-2xl p-8 lg:p-10 border-2 border-fastlane/20 hover:border-fastlane/40 transition-colors shadow-xl h-full flex flex-col">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-fastlane/10 rounded-full px-3 py-1 mb-6">
                <Zap className="w-4 h-4 text-fastlane" />
                <span className="text-fastlane text-sm font-semibold">ZEITKRITISCH</span>
              </div>

              {/* Icon */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-fastlane flex items-center justify-center mb-6 shadow-fastlane">
                <Zap className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <h3 className="font-heading font-bold text-2xl lg:text-3xl text-foreground mb-2">
                FastLane Express
              </h3>
              <p className="text-muted-foreground mb-6">
                Für zeitkritische Projekte mit sofortiger Bearbeitung
              </p>

              {/* Features */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-fastlane" />
                  <span className="text-foreground">Fertigung oft am selben Tag</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-fastlane" />
                  <span className="text-foreground">Mitarbeiter meldet sich in Minuten</span>
                </div>
                <div className="flex items-center gap-3">
                  <ArrowRight className="w-5 h-5 text-fastlane" />
                  <span className="text-foreground">Sofortige Machbarkeitsprüfung</span>
                </div>
              </div>

              {/* Process */}
              <div className="bg-fastlane/5 rounded-xl p-4 mb-8">
                <p className="text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">Prozess:</span> Kontaktaufnahme → Mitarbeiter meldet sich → Machbarkeitsprüfung → Express-Produktion
                </p>
              </div>

              <div className="mt-auto">
              <Button variant="fastlane" size="xl" className="w-full gap-2" asChild>
                <a href="/anfrage?type=fastlane">
                  <Zap className="w-5 h-5" />
                  FastLane aktivieren
                </a>
              </Button>
              </div>
            </div>
          </motion.div>

          {/* Smart Production Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative group h-full"
          >
            <div className="absolute inset-0 bg-gradient-smart rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity" />
            <div className="relative bg-card rounded-2xl p-8 lg:p-10 border-2 border-smart/20 hover:border-smart/40 transition-colors shadow-xl h-full flex flex-col">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-smart/10 rounded-full px-3 py-1 mb-6">
                <Brain className="w-4 h-4 text-smart" />
                <span className="text-smart text-sm font-semibold">KOSTENOPTIMIERT</span>
              </div>

              {/* Icon */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-smart flex items-center justify-center mb-6 shadow-smart">
                <Brain className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <h3 className="font-heading font-bold text-2xl lg:text-3xl text-foreground mb-2">
                Smart Production
              </h3>
              <p className="text-muted-foreground mb-6">
                Für kostenoptimierte Fertigung mit flexiblen Terminen
              </p>

              {/* Features */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <TrendingDown className="w-5 h-5 text-smart" />
                  <span className="text-foreground">Bis zu 40% Kostenersparnis</span>
                </div>
                <div className="flex items-center gap-3">
                  <Brain className="w-5 h-5 text-smart" />
                  <span className="text-foreground">Integration in laufende Projekte</span>
                </div>
                <div className="flex items-center gap-3">
                  <ArrowRight className="w-5 h-5 text-smart" />
                  <span className="text-foreground">Flexible Liefertermine</span>
                </div>
              </div>

              {/* Process */}
              <div className="bg-smart/5 rounded-xl p-4 mb-8">
                <p className="text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">Prozess:</span> Liefertermin angeben → Integration planen → Minimierte Produktionskosten
                </p>
              </div>

              <div className="mt-auto">
              <Button variant="smart" size="xl" className="w-full gap-2" asChild>
                <a href="/anfrage?type=smart">
                  <TrendingDown className="w-5 h-5" />
                  Kosten optimieren
                </a>
              </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceModelsSection;
